import { useBedsByBayId } from "../../hooks/useGreenhouseBeds";
import { CreateGreenhouseBedDialog } from "../beds/CreateGreenhouseBedDialog";
import type { GreenhouseBay } from "../../services/greenhouseBaysApi";
import { LoadingAnimation } from "@/shared/components/LoadingAnimation";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

interface ViewBayModalProps {
  bay: GreenhouseBay;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ViewBayModal = ({
  bay,
  open,
  onOpenChange,
}: ViewBayModalProps) => {
  const { data: beds = [], isLoading } = useBedsByBayId(bay.id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[min(90vw,896px)] max-w-none">
        <DialogHeader>
          <DialogTitle>{bay.bayName}</DialogTitle>
          <DialogDescription>
            {bay.bedCount} beds • {bay.sizeSquareMeters}m² • Capacity:{" "}
            {bay.trayCapacity}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {bay.description && (
            <div className="text-sm text-muted-foreground break-words">
              {bay.description}
            </div>
          )}

          <div className="flex justify-between items-center">
            <h3 className="font-semibold">Beds ({beds.length})</h3>
            <CreateGreenhouseBedDialog 
              bayId={bay.id}
              baySize={bay.sizeSquareMeters}
              existingBeds={beds}
            />
          </div>

          {isLoading ? (
            <LoadingAnimation text="Loading beds..." />
          ) : beds.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground">
              No beds created yet
            </div>
          ) : (
            <div className="rounded border overflow-hidden">
              <Table className="w-full table-fixed">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[35%]">Bed Number</TableHead>
                    <TableHead className="w-[35%]">Size (m²)</TableHead>
                    <TableHead className="w-[30%]">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {beds.map((bed) => (
                    <TableRow key={bed.id}>
                      <TableCell className="font-medium">
                        {bed.bedNumber}
                      </TableCell>
                      <TableCell className="text-sm">
                        {bed.sizeSquareMeters}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={bed.isActive ? "default" : "destructive"}
                        >
                          {bed.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

